/**
 * Tiny shared store for the in-app testing programme.
 *
 * - ``loadTestConfig(api)`` fetches /api/test-reports/config once per
 *   session (cached; ``refreshTestConfig`` forces a reload after staff
 *   change the settings) and notifies subscribers.
 * - ``openTestReport(preset)`` opens the report modal from ANYWHERE in the
 *   app (user menu, floating button, hotkey, or any page that wants a
 *   "Report this" link) without threading props around.
 *
 * Delete this folder to remove the feature (see label_studio/test_reports/README.md).
 */
const OPEN_EVENT = "afri:test-report-open";
const CONFIG_EVENT = "afri:test-report-config";

let config = null; // null = not loaded yet
let loading = null;
// The api instance of the last attempt, so an ``online`` event can retry
// without a component having to re-mount to hand us one.
let lastApi = null;

export const getTestConfig = () => config;

/**
 * Fetch the programme config, ONCE per session — but only cache a real
 * answer. A failed request (offline at boot, a transient 5xx, a flaky
 * phone connection) used to be cached as ``{enabled: false}`` for the
 * whole session, which silently hid the tester's checklist and report
 * entries until they force-quit the app: the menu gates on
 * ``config.enabled``, and nothing ever retried. Now a failure is
 * returned but NOT stored, so the next caller tries again, and coming
 * back online retries immediately.
 */
export const loadTestConfig = async (api, { force = false } = {}) => {
  lastApi = api ?? lastApi;
  if (config && !force) return config;
  if (loading && !force) return loading;
  loading = (async () => {
    let result;
    try {
      const res = await api.callApi("testReportConfig", { suppressError: true });
      // A well-formed response — including a genuine "programme is off" —
      // is authoritative and worth caching. Anything else is a failure.
      result = res && !res.error && typeof res.enabled === "boolean" ? res : null;
    } catch {
      result = null;
    }
    if (result) config = result;
    loading = null;
    const detail = result ?? { enabled: false, unavailable: true };
    window.dispatchEvent(new CustomEvent(CONFIG_EVENT, { detail }));
    return detail;
  })();
  return loading;
};

// Retry as soon as connectivity returns, so a tester who opened the app
// on a bad connection gets their checklist without restarting it. Also
// on focus/resume: a request can fail while ``navigator.onLine`` stayed
// true the whole time (captive portal, asleep radio, a one-off 502), and
// then no ``online`` event ever fires.
if (typeof window !== "undefined") {
  const retry = () => {
    if (!config && lastApi) loadTestConfig(lastApi, { force: true });
  };
  window.addEventListener("online", retry);
  window.addEventListener("focus", retry);
}

export const refreshTestConfig = (api) => loadTestConfig(api, { force: true });

export const subscribeTestConfig = (fn) => {
  const handler = (e) => fn(e.detail);
  window.addEventListener(CONFIG_EVENT, handler);
  return () => window.removeEventListener(CONFIG_EVENT, handler);
};

/** Open the report form. ``preset`` may carry {kind, step_id, module, title}. */
export const openTestReport = (preset = {}) => {
  window.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: preset }));
};

export const subscribeOpenTestReport = (fn) => {
  const handler = (e) => fn(e.detail || {});
  window.addEventListener(OPEN_EVENT, handler);
  return () => window.removeEventListener(OPEN_EVENT, handler);
};

/** Human page name from the document title ("Members | AfriAnnotate" -> "Members"). */
export const currentPageName = () => {
  const t = (typeof document !== "undefined" && document.title) || "";
  const first = t.split(" | ")[0].trim();
  if (first && first.toLowerCase() !== "afriannotate") return first;
  const path = (typeof window !== "undefined" && window.location.pathname) || "/";
  if (path === "/" || path === "") return "Home";
  const seg = path.split("/").filter(Boolean).pop() || "Home";
  return seg.replace(/[-_]+/g, " ").replace(/^\w/, (c) => c.toUpperCase());
};

/** Which client is this — for the "Where" field. */
export const detectSurface = () => {
  if (typeof window === "undefined") return "web";
  const w = window;
  const ua = navigator.userAgent || "";
  if (w.Capacitor || document.body?.classList?.contains("is-capacitor")) {
    return /iPhone|iPad|iPod|Macintosh/.test(ua) ? "ios" : "android";
  }
  if (w.__TAURI__ || w.__TAURI_INTERNALS__ || document.body?.classList?.contains("is-tauri")) {
    return /Windows/.test(ua) ? "desktop-windows" : "desktop-mac";
  }
  return "web";
};

/** Build label: app version when a client exposes one, else the server build. */
export const detectBuild = (serverBuild) => {
  if (typeof window === "undefined") return serverBuild || "";
  const w = window;
  const appVersion = w.APP_SETTINGS?.app_version || w.APP_SETTINGS?.version || w.__AFRI_APP_VERSION__ || "";
  const surface = detectSurface();
  if (appVersion && surface !== "web") return `${surface} ${appVersion}`;
  return serverBuild || appVersion || "";
};

export const guessModule = (hints) => {
  const path = (typeof window !== "undefined" && window.location.pathname) || "/";
  for (const [prefix, mod] of hints || []) {
    if (path.startsWith(prefix)) return mod;
  }
  return "other";
};
